import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Hero from "../../components/Hero/Hero";
import toast from "../../components/ui/toast";
import PageFooter from "../../components/PageFooter/PageFooter";
import InfoStats from "../Audit/InfoStats";
import ModalMoreInfo from "./ModalMoreInfoSL";
import StoreLocatorScoring from "../../components/StoreLocatorScoring/StoreLocatorScoring";
import ReCAPTCHA from "react-google-recaptcha";
import { Helmet } from "react-helmet";
import "./AuditStoreLocator.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/tech";

// Hard cap on the round-trip. The backend deadline is ~95s (scraper + Mistral),
// so 100s leaves a small margin for network. Anything longer means the proxy in
// front of the API likely killed the connection — fail fast with a useful UX
// instead of an infinite spinner.
const REQUEST_TIMEOUT_MS = 100000;

const LOADING_STEPS = [
  { delay: 0, label: "Chargement de la page", detail: "Rendu JavaScript et contenu dynamique" },
  { delay: 5000, label: "Extraction du contenu", detail: "Balises, images, liens et données structurées" },
  { delay: 10000, label: "Analyse SEO local", detail: "28 critères de référencement local" },
  { delay: 16000, label: "Évaluation des performances", detail: "Score global et pondération" },
  { delay: 22000, label: "Génération des recommandations", detail: "Analyse IA des forces et faiblesses" },
  { delay: 30000, label: "Finalisation", detail: "Préparation du rapport détaillé" },
];

const isValidUrl = (string) => {
  try {
    const url = new URL(string);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const AuditStoreLocator = () => {
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [result, setResult] = useState(null);
  const [displayResults, setDisplayResults] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isSA, setIsSA] = useState(false);
  const recaptchaRef = React.useRef();
  const stepTimersRef = useRef([]);
  const location = useLocation();

  useEffect(() => {
    if (location.search.includes("access=localranker")) {
      setIsSA(true);
    }
  }, [location.search]);

  const handleOpenModal = () => {
    const trimmed = url.trim();
    if (!trimmed) {
      toast.warning("Veuillez saisir une URL.");
      return;
    }
    if (!isValidUrl(trimmed)) {
      toast.warning(
        "Veuillez saisir une URL valide (ex: https://example.com/paris).",
      );
      return;
    }

    if (isSA) {
      submitAudit(trimmed);
    } else {
      setModalOpen(true);
    }
  };

  const submitAudit = async (auditUrl, businessInfo) => {
    setModalOpen(false);
    setDisplayResults(false);
    setResult(null);

    const tokenCaptcha = isSA ? true : await recaptchaRef.current?.executeAsync();
    if (!tokenCaptcha) {
      toast.warning("Veuillez résoudre le captcha pour accéder à l'audit.");
      return;
    }

    setIsLoading(true);
    setActiveStep(0);
    setAuditError(null);
    setDisplayResults(false);
    setResult(null);

    stepTimersRef.current = LOADING_STEPS.map(({ delay }, i) =>
      setTimeout(() => setActiveStep(i), delay),
    );

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const params = new URLSearchParams({ url: auditUrl });
      if (businessInfo) {
        params.append("businessInfo", JSON.stringify(businessInfo));
      } else if (isSA) {
        params.append("sa", "true");
      }

      const response = await fetch(
        `${API_URL}/store-locator-audit?${params.toString()}`,
        { method: "POST", signal: controller.signal },
      );

      if (response.status === 429) {
        setAuditError("Trop de requêtes effectuées, réessayez plus tard.");
        return;
      }

      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        setAuditError(
          "Réponse invalide du serveur. Réessayez dans quelques instants.",
        );
        return;
      }

      if (!response.ok) {
        setAuditError(data.error || "Une erreur est survenue lors de l'analyse.");
        return;
      }

      setResult(data);
      setDisplayResults(true);
    } catch (e) {
      console.error(e);
      if (e.name === "AbortError") {
        setAuditError(
          "L'analyse prend plus de temps que prévu. Réessayez dans quelques instants — si le problème persiste, le site analysé est peut-être trop lent.",
        );
      } else {
        setAuditError(
          "Impossible de contacter le serveur. Vérifiez votre connexion et réessayez.",
        );
      }
    } finally {
      clearTimeout(timeoutId);
      stepTimersRef.current.forEach(clearTimeout);
      stepTimersRef.current = [];
      setIsLoading(false);
      setActiveStep(0);
      recaptchaRef.current?.reset();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleOpenModal();
    }
  };

  return (
    <div>
      <Helmet>
        <title>
          Audit Store Locator gratuit - Analysez vos pages locales | LocalRanker
        </title>
        <meta
          name="description"
          content="Audit store locator gratuit : analysez le SEO local de vos pages locales. Vérifiez balises, données structurées, contenu et performance. Optimisez votre store locator pour le référencement local."
        />
        <link rel="canonical" href="https://localranker.tech/audit-store-locator" />
        <meta property="og:title" content="Audit Store Locator gratuit - Analysez vos pages locales | LocalRanker" />
        <meta property="og:description" content="Audit store locator gratuit : analysez le SEO local de vos pages locales. Vérifiez balises, données structurées, contenu et performance." />
        <meta property="og:url" content="https://localranker.tech/audit-store-locator" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Localranker" />
      </Helmet>
      <Hero
        title="Audit Store Locator"
        subtitle="Analysez une page locale de votre store locator. Collez l'URL d'une page locale pour obtenir un audit SEO détaillé : balises, données structurées, contenu, performance."
        ctaText="Lancer mon audit"
        ctaOnClick={() =>
          document
            .querySelector(".sl-search-form")
            ?.scrollIntoView({ behavior: "smooth", block: "center" })
        }
      />

      <div className="sl-search-form">
        <div className="search-form__card">
          <div className="search-form__row">
            <div className="search-form__input-wrap">
              <input
                type="url"
                placeholder="https://exemple.com/magasin/paris-11"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
              />
            </div>
            <button
              className="search-form__btn"
              type="button"
              onClick={handleOpenModal}
              disabled={isLoading}
            >
              {isLoading ? "Analyse..." : "Analyser"}
            </button>
          </div>
        </div>
      </div>

      {isLoading && (
        <AuditLoader url={url} steps={LOADING_STEPS} activeStep={activeStep} />
      )}

      <ReCAPTCHA
        sitekey="6LcLC9UZAAAAAOY_vgaU__i3Qhs1tmHtvbibjD2V"
        size="invisible"
        ref={recaptchaRef}
      />

      {!isLoading && auditError && (
        <div className="sla-error">
          <div className="sla-error__icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <p className="sla-error__msg">{auditError}</p>
          <button className="sla-error__retry" type="button" onClick={() => { setAuditError(null); handleOpenModal(); }}>
            Réessayer
          </button>
        </div>
      )}

      {!isLoading && !displayResults && !auditError && <InfoStats />}

      {displayResults && result && (
        <StoreLocatorScoring result={result} hasFullAccess={isSA} />
      )}

      <ModalMoreInfo
        visible={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={(data) => submitAudit(url.trim(), data)}
      />

      <section className="sl-seo">
        <div className="sl-seo__intro">
          <h2>Pourquoi auditer votre store locator ?</h2>
          <p>
            Un <strong>store locator</strong> bien optimisé est un levier majeur
            de <strong>référencement local</strong>. Chaque page locale est une
            porte d'entrée pour capter du trafic qualifié sur des requêtes
            géolocalisées — pourtant, la majorité présentent des lacunes SEO qui
            freinent leur visibilité sur Google.
          </p>
        </div>

        <div className="sl-seo__grid">
          <div className="sl-seo__card">
            <span className="sl-seo__card-icon">{"</>"}</span>
            <h3>Balises title & meta</h3>
            <p>
              Vérification de l'unicité et de la géolocalisation des balises
              pour chaque point de vente.
            </p>
          </div>
          <div className="sl-seo__card">
            <span className="sl-seo__card-icon">{"{}"}</span>
            <h3>Données structurées</h3>
            <p>
              Analyse du balisage Schema.org LocalBusiness : présence, notes
              agrégées (aggregateRating) et liens d'entité (sameAs).
            </p>
          </div>
          <div className="sl-seo__card">
            <span className="sl-seo__card-icon">Aa</span>
            <h3>Contenu local unique</h3>
            <p>
              Détection des pages dupliquées et évaluation de la différenciation
              du contenu local.
            </p>
          </div>
          <div className="sl-seo__card">
            <span className="sl-seo__card-icon">&#8693;</span>
            <h3>Maillage interne</h3>
            <p>
              Vérification du linking entre pages locales et du reste du site
              pour faciliter le crawl.
            </p>
          </div>
          <div className="sl-seo__card">
            <span className="sl-seo__card-icon">&#9889;</span>
            <h3>Performance</h3>
            <p>
              Analyse de la vitesse de chargement et des Core Web Vitals sur
              mobile.
            </p>
          </div>
          <div className="sl-seo__card">
            <span className="sl-seo__card-icon">&#127758;</span>
            <h3>SEO multi-établissements</h3>
            <p>
              Cohérence NAP, stratégie de mots-clés locaux et lien avec Google
              Business Profile.
            </p>
          </div>
        </div>

        <div className="sl-seo__block">
          <h3>Optimisation SEO des pages locales</h3>
          <p>
            L'<strong>optimisation SEO d'un store locator</strong> passe par un
            travail méthodique sur chaque page locale. Une page performante doit
            contenir les informations essentielles de l'établissement (adresse,
            horaires, téléphone), un contenu unique adapté à la zone de
            chalandise, et un balisage structuré complet. C'est la combinaison
            de ces éléments qui permet à vos pages de se positionner sur des
            requêtes comme "magasin + ville" ou "service + quartier".
          </p>
        </div>

        <div className="sl-seo__block">
          <h3>Développement store locator : les bonnes pratiques</h3>
          <p>
            Le <strong>développement d'un store locator</strong> performant ne
            se limite pas à afficher une carte et une liste d'adresses. Un store
            locator pensé pour le SEO local doit intégrer dès sa conception :
            des URLs propres par ville, un rendu côté serveur (SSR) pour
            l'indexation, un sitemap dédié aux pages locales, et une
            architecture qui facilite le crawl. Notre audit identifie les écarts
            entre votre implémentation et ces bonnes pratiques.
          </p>
        </div>

        <div className="sl-seo__faq">
          <h3>Questions fréquentes</h3>
          <div className="sl-seo__faq-item">
            <h4>Qu'est-ce qu'un audit store locator ?</h4>
            <p>
              Une analyse complète des pages locales de votre site : SEO
              on-page, données structurées, performance technique et pertinence
              du contenu pour chaque point de vente.
            </p>
          </div>
          <div className="sl-seo__faq-item">
            <h4>Combien de pages puis-je auditer ?</h4>
            <p>
              L'outil analyse une page locale à la fois pour vous permettre
              d'examiner et corriger les problèmes individuellement.
            </p>
          </div>
          <div className="sl-seo__faq-item">
            <h4>Mon store locator est-il bien indexé par Google ?</h4>
            <p>
              L'indexation dépend du sitemap, du maillage interne, du rendu
              JavaScript et de la vitesse de chargement. Notre audit identifie
              les blocages qui empêchent Google de découvrir vos pages locales.
            </p>
          </div>
        </div>
      </section>

      <PageFooter />
    </div>
  );
};

const AuditLoader = ({ url, steps, activeStep }) => {
  const progress = ((activeStep + 1) / steps.length) * 100;

  return (
    <div className="audit-loader">
      <div className="audit-loader__card">
        <div className="audit-loader__header">
          <div className="audit-loader__pulse" />
          <div>
            <p className="audit-loader__title">Analyse en cours</p>
            <p className="audit-loader__url">{url}</p>
          </div>
        </div>

        <div className="audit-loader__bar-track">
          <div
            className="audit-loader__bar-fill"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="audit-loader__steps">
          {steps.map((step, i) => {
            const done = i < activeStep;
            const active = i === activeStep;
            return (
              <div
                key={i}
                className={`audit-loader__step${done ? " audit-loader__step--done" : ""}${active ? " audit-loader__step--active" : ""}`}
              >
                <span className="audit-loader__step-dot">
                  {done ? (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2.5 6L5 8.5L9.5 3.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <span className="audit-loader__step-num">{i + 1}</span>
                  )}
                </span>
                <div className="audit-loader__step-text">
                  <span className="audit-loader__step-label">{step.label}</span>
                  {active && (
                    <span className="audit-loader__step-detail">
                      {step.detail}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AuditStoreLocator;
